import clsx from 'clsx';
import { isSameMonth, isToday } from 'date-fns';
import EventChip from './EventChip';
import type { CalendarEventInstance } from './types';

const MAX_CHIPS = 3;

interface Props {
  date: Date;
  month: Date;
  instances: CalendarEventInstance[];
  onSelectEvent: (instance: CalendarEventInstance) => void;
  onSelectDay: (date: Date) => void;
  /** Leader-only; undefined hides the create affordance. */
  onCreateOnDay?: (date: Date) => void;
  onChipMouseEnter?: (instance: CalendarEventInstance, e: React.MouseEvent<HTMLElement>) => void;
  onChipMouseLeave?: () => void;
}

export default function CalendarDayCell({
  date,
  month,
  instances,
  onSelectEvent,
  onSelectDay,
  onCreateOnDay,
  onChipMouseEnter,
  onChipMouseLeave,
}: Props) {
  const today = isToday(date);
  const inMonth = isSameMonth(date, month);
  const visible = instances.slice(0, MAX_CHIPS);
  const overflow = instances.length - visible.length;

  return (
    <div
      onClick={(e) => {
        // Only empty-cell clicks create; chip clicks stop propagation upstream.
        if (onCreateOnDay && e.target === e.currentTarget) onCreateOnDay(date);
      }}
      className={clsx(
        'group/cell flex min-h-0 flex-col gap-1 overflow-hidden border-b border-r border-flock-gray-200 p-1.5',
        !inMonth && 'bg-flock-navy-50/40',
        onCreateOnDay && 'cursor-pointer hover:bg-flock-navy-50/70'
      )}
    >
      <div className="pointer-events-none flex items-center justify-between">
        <span
          className={clsx(
            'flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium',
            today
              ? 'bg-flock-navy-600 font-semibold text-white'
              : inMonth
                ? 'text-flock-navy-700'
                : 'text-flock-navy-300'
          )}
        >
          {date.getDate()}
        </span>
        {onCreateOnDay && (
          <span className="hidden text-[10px] text-flock-navy-300 group-hover/cell:inline">+</span>
        )}
      </div>

      <div className="flex min-h-0 flex-col gap-1" onClick={(e) => e.stopPropagation()}>
        {visible.map((instance) => (
          <EventChip
            key={instance.instanceKey}
            instance={instance}
            variant="chip"
            onClick={() => onSelectEvent(instance)}
            onMouseEnter={onChipMouseEnter ? (e) => onChipMouseEnter(instance, e) : undefined}
            onMouseLeave={onChipMouseLeave}
          />
        ))}
        {overflow > 0 && (
          <button
            onClick={() => onSelectDay(date)}
            className="rounded-md px-1.5 py-0.5 text-left text-[11px] font-medium text-flock-navy-500 transition hover:bg-flock-navy-50 hover:text-flock-navy-700"
          >
            +{overflow} more
          </button>
        )}
      </div>
    </div>
  );
}
